'use client';

import { Root as SlotRoot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import * as React from 'react';

import { DotsSlantIcon } from '../Loading/Loading';
import styles from './Button.module.css';

/** Button display type, matching Figma "Type" property. */
type ButtonVariant = 'filled' | 'outlined' | 'text';

/**
 * Semantic color of the button, matching Figma "Semantic Color" property.
 * `link` is only meaningful with `variant="text"`.
 */
type ButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'link';

/** Button size, matching Figma "Size" property. */
type ButtonSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ComponentPropsWithRef<'button'> {
  /** Visual style of the button. @default 'filled' */
  variant?: ButtonVariant;
  /** Semantic color of the button. @default 'primary' */
  color?: ButtonColor;
  /** Size of the button. @default 'md' */
  size?: ButtonSize;
  /**
   * Icon element to render inside the button.
   * When provided without `children`, the button enters icon-only mode
   * (square container). In that case, supply `aria-label` for accessibility.
   */
  icon?: React.ReactNode;
  /** Position of the icon relative to label text. @default 'start' */
  iconPosition?: 'start' | 'end';
  /**
   * When true, shows the vsds-icon/dots-slant loading spinner and disables
   * the button. Any label text is kept invisible in the layout so the button
   * maintains its natural width. Pair with `aria-label` to convey loading
   * context to screen readers.
   */
  loading?: boolean;
  /**
   * When true, renders the button's child as the root element via Radix Slot,
   * merging all button props and event handlers onto it.
   */
  asChild?: boolean;
}

const sizeStyleMap: Record<ButtonSize, string> = {
  '2xs': styles.size2xs,
  xs: styles.sizeXs,
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

const iconSizeStyleMap: Record<ButtonSize, string> = {
  '2xs': styles.iconSize2xs,
  xs: styles.iconSizeXs,
  sm: styles.iconSizeSm,
  md: styles.iconSizeMd,
  lg: styles.iconSizeLg,
};

// Variant × color class maps
const filledStyleMap: Record<ButtonColor, string> = {
  default: styles.filledDefault,
  primary: styles.filledPrimary,
  secondary: styles.filledSecondary,
  accent: styles.filledAccent,
  neutral: styles.filledNeutral,
  info: styles.filledInfo,
  success: styles.filledSuccess,
  warning: styles.filledWarning,
  danger: styles.filledDanger,
  link: styles.filledLink,
};

const outlinedStyleMap: Record<ButtonColor, string> = {
  default: styles.outlinedDefault,
  primary: styles.outlinedPrimary,
  secondary: styles.outlinedSecondary,
  accent: styles.outlinedAccent,
  neutral: styles.outlinedNeutral,
  info: styles.outlinedInfo,
  success: styles.outlinedSuccess,
  warning: styles.outlinedWarning,
  danger: styles.outlinedDanger,
  link: styles.outlinedLink,
};

const textStyleMap: Record<ButtonColor, string> = {
  default: styles.textDefault,
  primary: styles.textPrimary,
  secondary: styles.textSecondary,
  accent: styles.textAccent,
  neutral: styles.textNeutral,
  info: styles.textInfo,
  success: styles.textSuccess,
  warning: styles.textWarning,
  danger: styles.textDanger,
  link: styles.textLink,
};

const variantStyleMap: Record<ButtonVariant, Record<ButtonColor, string>> = {
  filled: filledStyleMap,
  outlined: outlinedStyleMap,
  text: textStyleMap,
};

/**
 * Button component for triggering user actions.
 *
 * Supports three visual variants (filled, outlined, text), ten semantic colors,
 * five sizes, icon positioning (start/end/icon-only), and polymorphic rendering
 * via `asChild`. Fully accessible via keyboard and screen reader.
 *
 * @example
 * // Basic usage
 * <Button>Save</Button>
 *
 * @example
 * // Danger outlined
 * <Button variant="outlined" color="danger">Delete</Button>
 *
 * @example
 * // Icon-only (aria-label required)
 * <Button icon={<PlusIcon />} aria-label="Add item" />
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      color = 'primary',
      size = 'md',
      icon,
      iconPosition = 'start',
      loading = false,
      asChild = false,
      className,
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    // `link` color is only meaningful with text variant; force it regardless of what was passed.
    const resolvedVariant = color === 'link' ? 'text' : variant;
    const isIconOnly = icon !== undefined && children === undefined;
    const rootClass = [
      styles.root,
      sizeStyleMap[size],
      // Loading overrides variant colors with the disabled surface.
      loading ? styles.loading : variantStyleMap[resolvedVariant][color],
      isIconOnly ? styles.iconOnly : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ');
    const iconClass = [styles.icon, iconSizeStyleMap[size]]
      .filter(Boolean)
      .join(' ');
    const spinnerClass = [styles.spinner, iconSizeStyleMap[size]]
      .filter(Boolean)
      .join(' ');

    const sharedProps = {
      ref,
      disabled: loading || disabled,
      'aria-busy': loading || undefined,
      className: rootClass,
      ...props,
    };

    // asChild: merge props onto a single child element — icons are not rendered in this mode.
    if (asChild) {
      return <SlotRoot {...sharedProps}>{children}</SlotRoot>;
    }

    return (
      <button type={type} {...sharedProps}>
        {loading ? (
          <>
            {/* Keep label invisible in the layout so the button maintains its natural width. */}
            {children !== undefined && (
              <span aria-hidden="true" style={{ visibility: 'hidden' }}>
                {children}
              </span>
            )}
            {/* Spinner positioned absolutely at center, sized to match the icon slot. */}
            <span aria-hidden="true" className={spinnerClass}>
              <DotsSlantIcon />
            </span>
          </>
        ) : (
          <>
            {icon !== undefined && iconPosition === 'start' && (
              <span aria-hidden="true" className={iconClass}>
                {icon}
              </span>
            )}
            {children}
            {icon !== undefined && iconPosition === 'end' && (
              <span aria-hidden="true" className={iconClass}>
                {icon}
              </span>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
